import { useEffect, useState } from 'react'
import { useWorkspace } from '../store/AppContext'
import { Toggle } from '../components/ui'
import { chargesForTime } from '../lib/billing'
import { initials, pluralise } from '../lib/util'
import { hms } from '../lib/time'
import { ROLE_LABEL, type ID, type Role } from '../lib/types'
import * as db from '../lib/db'
import { useRangeEntries, useRunningTimers } from '../lib/team-data'
import {
  AccessRequestsPanel,
  RateRow,
  useAccessRequests,
  useRates,
} from './team-extras'

export function TeamScreen() {
  const { ws, me, act, canManage, canSeeAll, canSetRates, setModal, flash, confirm, myWeek, proj } =
    useWorkspace()
  const week = myWeek(0)

  // Utilisation needs everyone's week, which the snapshot no longer carries.
  // One bounded fetch of seven days beats shipping the whole history.
  const { entries: weekEntries } = useRangeEntries(
    ws.id,
    { from: week[0], to: week[week.length - 1] },
    canSeeAll,
  )
  // Who is on the clock this second. At most one row per person, and it stops
  // being asked for the moment this screen closes.
  const runningNow = useRunningTimers(ws.id, true)

  /*
   * The elapsed counters tick only while somebody is actually on the clock.
   * Reading `now` from the context would have been shorter, but it changes
   * every second whether or not this screen has anything to count, and a
   * permanently re-rendering team grid is the kind of idle cost worth not
   * adding to a screen that is otherwise static.
   */
  const [showRetired, setShowRetired] = useState(false)
  const [tickNow, setTickNow] = useState(() => Date.now())
  const anyRunning = runningNow.size > 0

  useEffect(() => {
    if (!anyRunning) return
    const id = setInterval(() => setTickNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [anyRunning])

  // Retired people stay in ws.team so their past hours still name somebody,
  // but they are not part of the team any more and must not be counted as it.
  const retired = ws.team.filter((m) => m.status === 'retired')
  const current = ws.team.filter((m) => m.status !== 'retired')
  const pending = current.filter((m) => m.status === 'pending').length
  const active = current.length - pending
  const [busy, setBusy] = useState(false)

  const { requests, reload: reloadRequests } = useAccessRequests(ws.id, canManage)
  const { rates, reload: reloadRates } = useRates(ws.id)

  const decide = (id: ID, approve: boolean, role: Role) => {
    if (busy) return
    setBusy(true)
    const work = approve ? db.approveRequest(ws.id, id, role, 40) : db.rejectRequest(id)
    void work
      .then(async () => {
        flash(approve ? 'Access approved' : 'Request rejected')
        await reloadRequests()
        // Approval creates a membership, so the roster has changed too.
        if (approve) await act.updateWorkspace({})
      })
      .catch((err: unknown) => flash(err instanceof Error ? err.message : 'That did not work'))
      .finally(() => setBusy(false))
  }

  /*
   * Ask for a link, then put it where the owner can actually use it.
   *
   * Copied to the clipboard on the happy path, because the only thing anyone
   * wants to do with a link is paste it. Shown in full when that fails -- the
   * clipboard needs a secure context and a permission, and a link nobody can
   * reach is the same as no link at all.
   */
  const passwordLink = async (userId: ID, name: string) => {
    const url = await act.resetLinkFor(userId)
    if (!url) return

    const note =
      `Send this to ${name}. It works once, expires in an hour, and lets them ` +
      'choose a new password. Anyone holding it can do that, so pass it on the ' +
      'way you would a password.'

    try {
      await navigator.clipboard.writeText(url)
      flash('Link copied. ' + note)
    } catch {
      confirm({
        title: `Password link for ${name}`,
        body: note + ' ' + url,
        cta: 'Done',
        onConfirm: () => {},
      })
    }
  }

  const saveRate = (userId: ID, amount: number, effectiveFrom: string, currency: string) => {
    if (busy) return
    setBusy(true)
    void db
      .setRate(ws.id, userId, amount, effectiveFrom, currency)
      .then(async () => {
        flash('Rate saved')
        await reloadRates()
      })
      .catch((err: unknown) => flash(err instanceof Error ? err.message : 'Could not save the rate'))
      .finally(() => setBusy(false))
  }

  return (
    <section className="screen">
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--mut)' }}>
          {pluralise(active, 'person', 'people')} · {pluralise(pending, 'pending invite')}
        </div>
        {canManage && (
          <button type="button" className="btn btn--primary push" onClick={() => setModal({ kind: 'invite' })}>
            + Invite by email
          </button>
        )}
      </div>

      {canManage && (
        <AccessRequestsPanel
          requests={requests}
          onDecide={decide}
          busy={busy}
          acceptsSignups={ws.acceptsSignups}
        />
      )}

      <div className="card-grid card-grid--wide">
        {current.map((m) => {
          const isPending = m.status === 'pending'
          const source = canSeeAll ? weekEntries : ws.entries
          const mine = source.filter((e) => e.userId === m.id && week.includes(e.day))
          const sec = mine.reduce((a, e) => a + e.dur, 0)
          const bill = mine
            .filter((e) => chargesForTime(e.billable, m.billable, proj(e.projectId)?.billable))
            .reduce((a, e) => a + e.dur, 0)
          const isSelf = m.id === me.id
          const isOwner = m.role === 'owner'
          const live = runningNow.get(m.id)
          const liveFor = live ? Math.max(0, Math.floor((tickNow - live.at) / 1000)) : 0
          const liveOn = live ? (proj(live.projectId)?.name ?? live.desc.trim()) : ''

          return (
            <div key={m.id} className="proj-card">
              <div className="row" style={{ gap: 11 }}>
                <span className="member-avatar-wrap">
                  <span
                    className={isPending ? 'member-avatar member-avatar--pending' : 'member-avatar'}
                    aria-hidden
                  >
                    {initials(m.name)}
                  </span>
                  {live && (
                    <span
                      className="presence-dot"
                      title={`Tracking now — ${hms(liveFor)}${liveOn ? ` on ${liveOn}` : ''}`}
                    />
                  )}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="trunc" style={{ fontWeight: 600, fontSize: 14.5 }}>
                    {m.name}
                    {isSelf && <span style={{ color: 'var(--faint)', fontWeight: 400 }}> (you)</span>}
                  </div>
                  <div className="trunc" style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                    {m.email}
                  </div>
                  {live && (
                    <div className="row" style={{ gap: 8, marginTop: 2, minWidth: 0 }}>
                      <span
                        className="trunc mono"
                        style={{ fontSize: 12, color: 'var(--gr)', minWidth: 0 }}
                      >
                        Tracking {hms(liveFor)}
                        {liveOn ? ` · ${liveOn}` : ''}
                      </span>
                      {/*
                       * For the timer somebody left running on a laptop they
                       * closed on Friday. The ten-hour limit is applied when a
                       * timer STOPS, and the thing that stops one at the limit
                       * is a client effect running in that person's own
                       * browser -- so while they are away nothing ends it and
                       * the count on this card keeps climbing past the limit.
                       *
                       * Not shown on your own card: your own Stop is on the
                       * timer bar, where it belongs, and a second one here
                       * that reads as an administrative act would be an odd
                       * way to end your own afternoon.
                       */}
                      {canSeeAll && !isSelf && (
                        <button
                          type="button"
                          className="linkbtn"
                          style={{ fontSize: 12, color: 'var(--dg)', flexShrink: 0 }}
                          onClick={() =>
                            confirm({
                              title: `Stop ${m.name}'s timer?`,
                              body:
                                `It has been running ${hms(liveFor)}. The time is saved as an ` +
                                'entry on their behalf, ending now' +
                                (liveFor > 10 * 3600
                                  ? ' -- capped at ten hours, which is the longest a single entry can be.'
                                  : '.') +
                                ' They can correct or delete it afterwards.',
                              cta: 'Stop the timer',
                              danger: true,
                              onConfirm: () => void act.stopTimerFor(m.id, m.name),
                            })
                          }
                        >
                          Stop
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {canManage && !isOwner && !isPending ? (
                  <select
                    className="role-select"
                    value={m.role}
                    aria-label={`Role for ${m.name}`}
                    onChange={(e) => void act.updateMember(m.id, { role: e.target.value as Role })}
                  >
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="member">Member</option>
                  </select>
                ) : (
                  <span className="status status--off">{ROLE_LABEL[m.role]}</span>
                )}
              </div>

              {/* A member can only read their own entries, so a colleague's bar
                  would sit at a confident 0.0h. Better to show nothing than a
                  figure that is wrong. */}
              {!isPending && (canSeeAll || isSelf) && (
                <div>
                  <div className="spread" style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 6 }}>
                    <span>This week</span>
                    <span className="mono">{(sec / 3600).toFixed(1)}h</span>
                  </div>
                </div>
              )}

              {/* Rates are readable by the whole team now, and a person may
                  set their own. Admins may still set anybody's. */}
              {!isPending && canManage && (
                <div
                  className="spread"
                  style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}
                >
                  <span style={{ fontSize: 12, color: 'var(--mut)' }}>
                    Charges for time
                    {!m.billable && (
                      <span style={{ color: 'var(--dg)', marginLeft: 6 }}>— not charging</span>
                    )}
                  </span>
                  <Toggle
                    checked={m.billable}
                    label={`${m.name} charges for time`}
                    onChange={(v) => {
                      // Switching it on needs no ceremony: it restores charging,
                      // and a figure that reappears gets checked anyway.
                      if (v) {
                        void act.updateMember(m.id, { billable: true })
                        return
                      }

                      /*
                       * Off is the one worth stopping for, and it reaches
                       * further than the switch suggests. entry_billing ANDs
                       * this flag into every row and forces the amount to
                       * zero, so it is not "from now on" -- it silently
                       * un-charges everything this person has ever logged.
                       * The confirmation says so, because the toggle cannot.
                       */
                      confirm({
                        title: `Stop charging for ${m.name}'s time?`,
                        body:
                          `Every hour ${m.name} has ever logged stops counting towards an ` +
                          'invoice, not only the hours from here on -- whatever each entry ' +
                          'says about itself, and whatever its project says. Reports and ' +
                          'exports will show their time at zero. ' +
                          'Their charge-out rate is kept, so switching this back puts every ' +
                          'amount exactly as it was.',
                        cta: 'Stop charging',
                        danger: true,
                        onConfirm: () => void act.updateMember(m.id, { billable: false }),
                      })
                    }}
                  />
                </div>
              )}

              {/*
                  A link, not a password. The owner never learns what the
                  person chooses, and the person still chooses it -- which is
                  the difference between helping somebody back in and being
                  able to sign in as them afterwards.

                  It exists because the emailed link keeps arriving spent:
                  mail systems open every link they carry, and the token is
                  redeemed by whatever opens it. This hands the link over
                  directly instead.
              */}
              {!isPending && canManage && (
                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busy}
                    title={`Create a one-time link for ${m.name} to set a new password`}
                    onClick={() => void passwordLink(m.id, m.name)}
                  >
                    Reset password
                  </button>
                </div>
              )}

              {!isPending && (
                <RateRow
                  userId={m.id}
                  userName={m.name}
                  rates={rates}
                  canEdit={canSetRates || isSelf}
                  onSave={saveRate}
                  busy={busy}
                />
              )}

              <div className="proj-foot">
                <span className={isPending ? 'status status--pend' : 'status status--on'}>
                  {isPending ? 'Invite sent' : isOwner ? 'Owner' : 'Active'}
                </span>
                {!isPending && (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {sec ? `${Math.round((bill / sec) * 100)}% billable` : 'No hours yet'}
                  </span>
                )}
                <span className="push" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {canManage && !isPending && !isOwner && (
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => setModal({ kind: 'member', member: m })}
                    >
                      Capacity
                    </button>
                  )}
                  {canManage && !isPending && !isSelf && !isOwner && (
                    <button
                      type="button"
                      className="btn btn--sm btn--danger"
                      onClick={() =>
                        confirm({
                          title: `Remove ${m.name}?`,
                          body:
                            `They lose access to ${ws.name} and can no longer sign in. ` +
                            'The hours they logged stay, so past reports and invoices are ' +
                            'unchanged, and their name stays on them. ' +
                            `You can bring ${m.name} back later from Former members, with ` +
                            'their history intact.',
                          cta: 'Remove person',
                          danger: true,
                          // Removing somebody is not a click to undo by
                          // clicking again, even though it is now reversible --
                          // it signs them out everywhere and takes an admin to
                          // put right.
                          requireText: m.name,
                          onConfirm: () => void act.removeMember(m.id),
                        })
                      }
                    >
                      Remove
                    </button>
                  )}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Folded away by default: this list only grows, and after a few years
          of turnover it would be the largest thing on a screen about the
          people who are actually here. The count stays visible so it is
          still findable. */}
      {canManage && retired.length > 0 && (
        <div className="card card--pad">
          <button
            type="button"
            aria-expanded={showRetired}
            onClick={() => setShowRetired((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              width: '100%',
              padding: 0,
              border: 0,
              background: 'transparent',
              font: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden
              style={{
                fontSize: 10,
                color: 'var(--mut)',
                transform: showRetired ? 'none' : 'rotate(-90deg)',
                display: 'inline-block',
                width: 10,
              }}
            >
              ▾
            </span>
            <span className="card-title">Former members</span>
            <span style={{ fontSize: 12.5, color: 'var(--mut)' }}>{retired.length}</span>
          </button>

          {showRetired && (
            <>
              <p
                className="pretty"
                style={{ fontSize: 12.5, color: 'var(--mut)', margin: '10px 0 12px' }}
              >
                Removed, and no longer able to sign in. They are listed because the hours they
                logged are still in your reports and exports, and those hours need a name. Their
                email addresses have been released, so they can sign up again as new people.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {retired.map((m) => (
                  <span
                    key={m.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12.5,
                      color: 'var(--mut)',
                      background: 'var(--canvas)',
                      border: '1px solid var(--line)',
                      borderRadius: 999,
                      padding: '4px 6px 4px 11px',
                    }}
                  >
                    {m.name}
                    <button
                      type="button"
                      className="linkbtn"
                      title={`Bring ${m.name} back into ${ws.name}`}
                      onClick={() =>
                        confirm({
                          title: `Bring ${m.name} back?`,
                          body:
                            `They rejoin ${ws.name} as a member and can sign in again with ` +
                            'their original email. The hours they logged while they were away ' +
                            'from the team were never removed, so their history is already there.',
                          cta: 'Restore person',
                          onConfirm: () => void act.restoreMember(m.id),
                        })
                      }
                    >
                      Restore
                    </button>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div style={{ fontSize: 12.5, color: 'var(--mut)' }} className="pretty">
        {canManage
          ? 'Everyone signs in with their own account and tracks their own time. As owner or admin you see every entry here and in Reports, and can export the whole workspace’s hours and cost.'
          : 'Everyone tracks their own time. Owners and managers see the workspace totals.'}
      </div>
    </section>
  )
}
